import {
  useMutation,
  UseMutationResult,
  useQuery,
} from "@tanstack/react-query";
import { queryKeys } from "../constants";
import {
  getAccounts,
  getAccountById,
  createAccount,
  updateAccount,
  deleteAccount,
} from "services";
import { handleAPIError, queryClient } from "utils";
import { Account } from "types";

// Fetch all accounts
export const useGetAccounts = (branch_id?: string | number) => {
  const queryResult = useQuery({
    queryKey: [queryKeys.accounts, branch_id],
    queryFn: async () => await getAccounts(branch_id),
  });

  if (queryResult.isError) {
    handleAPIError(queryResult.error, true);
  }

  return queryResult;
};

// Fetch a single account by ID
export const useGetAccountById = (id: string) => {
  return useQuery({
    queryKey: [queryKeys.accounts, id],
    queryFn: async () => await getAccountById(id),
    enabled: !!id,
  });
};

// Create a new account
export const useCreateAccount = () => {
  return useMutation({
    mutationFn: createAccount,
    onSuccess: async (data) => {
      console.log("Account created successfully:", data);
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.accounts],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};

type UpdateAccountPayload = {
  id: string;
  data: Account;
};

// Update account hook
export const useUpdateAccount = (): UseMutationResult<
  any,
  Error,
  UpdateAccountPayload
> => {
  return useMutation<any, Error, UpdateAccountPayload>({
    mutationFn: ({ id, data }: UpdateAccountPayload) => updateAccount(id, data),
    onSuccess: async () => {
      // Invalidate accounts query on success
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.accounts],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};

// Delete account hook
export const useDeleteAccount = () => {
  return useMutation({
    mutationFn: deleteAccount,
    onSuccess: async () => {
      console.log("Account deleted successfully");
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.accounts],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};
